import React from "react";
import { Flex, useDisclosure, Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import CreateClassroomModal from "js/components/ClassroomCreate";
import useStore from "stores/index";
import { CombineClassesModal } from "../CombineClasses/CombineClassesModal";
import { FocusLibraryDrawer } from "../../../components/FocusLibraryDrawer";
import { getFeatures } from "stores/classwizeConfigStore";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { RulesLibraryDrawer } from "@/components/RulesLibraryDrawer";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

interface Props {
	teacherClassroomsEnabled: boolean;
	isMonitoringAuthorised: boolean;
	teacherClassroomsSyncEnabled: boolean;
	onSyncClick: () => void;
}

export function CtxBarRight(props: Props): JSX.Element {
	const { t } = useTranslation();
	const createClassModal = useDisclosure();
	const combineClassesModal = useDisclosure();
	const focusLibraryDrawer = useDisclosure();
	const rulesLibraryDrawer = useDisclosure();

	async function handleSubmitCreateClassroom(classroomName: string, isMonitoringClass: boolean, isPinned: boolean) {
		return await useStore.getState().classroom.handleAddClassroom({
			name: classroomName,
			isMonitoringClass,
			pinned: isPinned,
		});
	}

	const activeDevice = useUserSessionStore((state) => state.active_device);
	const featureFlagsQuery = useFeatureFlagsQuery(activeDevice as string);

	const showFocusLibraryButton = getFeatures().includes("focus");
	const showRulesLibraryButton = !!featureFlagsQuery.data?.includes(FeatureFlag.RULES_LIBRARY);

	return (
		<>
			<Flex columnGap={2} ml={"auto"}>
				{props.teacherClassroomsEnabled && (
					<Button size="small" onClick={createClassModal.onOpen} aria-label={t("overview.addClass")}>
						<ChakraFAIcon icon={"fa-plus"} variant="solid" mr={2} boxSize={4} />
						{t("overview.addClass")}
					</Button>
				)}
				<Button size="small" onClick={combineClassesModal.onOpen} aria-label={t("overview.combineClasses")}>
					<ChakraFAIcon icon={"fa-merge"} variant="solid" mr={2} boxSize={4} />
					{t("overview.combineClasses")}
				</Button>
				{showFocusLibraryButton && (
					<>
						<Button
							size="small"
							data-testid="focus-library-button"
							onClick={focusLibraryDrawer.onOpen}
							aria-label={t("focusLibrary.libraryTitle")}
						>
							<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" mr={2} boxSize={4} />
							{t("focusLibrary.libraryTitle")}
						</Button>
						<FocusLibraryDrawer isOpen={focusLibraryDrawer.isOpen} closeDrawer={focusLibraryDrawer.onClose} />
					</>
				)}
				{showRulesLibraryButton && (
					<>
						<Button
							size="small"
							data-testid="rules-library-button"
							onClick={rulesLibraryDrawer.onOpen}
							aria-label={t("rulesLibrary.title")}
						>
							<ChakraFAIcon icon={"fa-books"} variant="solid" mr={2} boxSize={4} />
							{t("rulesLibrary.title")}
						</Button>
						<RulesLibraryDrawer isOpen={rulesLibraryDrawer.isOpen} closeDrawer={rulesLibraryDrawer.onClose} />
					</>
				)}
				{props.teacherClassroomsSyncEnabled && (
					<Button title={t("overview.syncGoogleClasses")} onClick={props.onSyncClick} size={"small"}>
						<ChakraFAIcon icon={"fa-refresh"} variant="solid" mr={2} boxSize={4} />
						{t("overview.sync")}
					</Button>
				)}
			</Flex>

			<CreateClassroomModal
				open={createClassModal.isOpen}
				onClose={createClassModal.onClose}
				heading={t("overview.createNewClass")}
				isMonitoringAuthorised={props.isMonitoringAuthorised}
				onSubmit={handleSubmitCreateClassroom}
				bodyClass="addClass_modal"
			/>
			<CombineClassesModal open={combineClassesModal.isOpen} onClose={combineClassesModal.onClose} />
		</>
	);
}
